import { useEffect, useState } from 'react';

import {
  FileReportSendResponseViewModel,
  FileReportUploadResponse,
} from '@fora-monorepo/employer-my-pages-common/types';
import { swedishNumberFormatter } from '@fora-monorepo/string-utils';
import { getReadableMonthAndYearFromPayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import {
  Assets,
  Button,
  ButtonTextOrIcon,
  Checkbox,
  Container,
  FAQ,
  Feedback,
  Headings,
  Panel,
  QuestionsAndAnswersType,
  Text,
} from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { useAppSelector } from '../../redux/hooks';
import { useTypedTranslation } from '../../i18n/i18n';
import { FileReportTempTier1ErrorMessage } from './FileReportTempTier1ErrorMessage';

interface FileReportingReviewAndConfirmPanelProps {
  devLogging?: boolean;
  fileResponse: FileReportUploadResponse | null;
  loading: boolean;
  receiptResponse: FileReportSendResponseViewModel | null;
  faqs: QuestionsAndAnswersType[];
  sendConfirmation: () => void;
}

export const FileReportingReviewAndConfirmPanel = ({
  devLogging,
  fileResponse,
  loading,
  receiptResponse,
  faqs,
  sendConfirmation,
}: FileReportingReviewAndConfirmPanelProps) => {
  const { reviewAndConfirmPanelState } = useAppSelector(state => state.file);
  const [sendButtonDisabled, setSendButtonDisabled] = useState(false);
  const [consent, setConsent] = useState(false);
  const { t } = useTypedTranslation();
  const loadingSendFileReport = useAppSelector(state => state.file.loadingSendFileReport);

  useEffect(() => {
    if (!loading && reviewAndConfirmPanelState.isOpen) {
      setSendButtonDisabled(false);
    }
  }, [loading, reviewAndConfirmPanelState.isOpen]);

  useEffect(() => {
    // Reset consent
    setConsent(false);
  }, [reviewAndConfirmPanelState.isOpen]);

  const handleSendConfirmation = () => {
    sendConfirmation();
    setSendButtonDisabled(true);
  };

  return (
    <Panel {...reviewAndConfirmPanelState} previousPanelName="panel-file-upload" name="panel-review-confirm">
      <Panel.Header>{t('panel.reviewAndConfirm.panelHeader')}</Panel.Header>
      <Panel.Body>
        <Panel.MainSection>
          {fileResponse?.fileReportSummaries?.map((eachSummary, i) => (
            <Container key={i} marginBottom={60}>
              <Text size="medium" color="black70" fontWeight="semibold" marginBottom={20} marginTop={20}>
                {t('common.organizationNameAndNumber', {
                  organizationName: eachSummary.organizationName,
                  orgNumber: eachSummary.organizationNumber,
                })}
              </Text>
              {eachSummary.totalPayrollsReported !== 0 && (
                <>
                  <Headings.H3 marginBottom={16}> {t('panel.reviewAndConfirm.title')}</Headings.H3>
                  {eachSummary.reportPeriods?.map((eachPeriod, i) => (
                    <Container key={i}>
                      <Headings.H6>{getReadableMonthAndYearFromPayMonth(eachPeriod.payMonth)}</Headings.H6>

                      <Text marginBottom={16}>
                        {t('panel.reviewAndConfirm.monthlyTotal', {
                          count: eachPeriod.payrollsReported,
                          totalPayrolls: eachPeriod.payrollsReported,
                          totalPayAmount: swedishNumberFormatter(eachPeriod.payrollAmount),
                        })}
                      </Text>
                    </Container>
                  ))}
                </>
              )}

              {eachSummary.totalEmploymentsReported !== 0 && (
                <>
                  <Headings.H3 marginTop={30} marginBottom={6}>
                    {t('panel.reviewAndConfirm.employeeInfoTitle')}
                  </Headings.H3>
                  <Text>
                    {t('panel.reviewAndConfirm.employeeInfoText', {
                      totalEmploymentsReported: eachSummary.totalEmploymentsReported,
                      count: eachSummary.totalEmploymentsReported,
                    })}
                  </Text>
                </>
              )}
            </Container>
          ))}

          <Feedback type="warning" iconAbove={true} negativeSideMargins={true} marginBottom={50}>
            <Text size="small" fontWeight="bold" marginBottom={5}>
              {t('panel.reviewAndConfirm.employmentPeriodsFeatureMissingTitle')}
            </Text>
            <Text size="small">{t('panel.reviewAndConfirm.employmentPeriodsFeatureMissingDescription')}</Text>
          </Feedback>

          <Container maxWidth={600}>
            <Checkbox
              id="id-checkbox-story-book-default"
              checked={consent}
              hasError={false}
              onChange={() => {
                setConsent(!consent);
              }}
              topAlignCheckbox={true}
            >
              {t('panel.reviewAndConfirm.checkboxDescription')}
            </Checkbox>
            <Button
              disabled={!consent || sendButtonDisabled}
              variant={consent ? 'primary' : 'grey'}
              style={{ marginTop: 60 }}
              onClick={handleSendConfirmation}
            >
              <ButtonTextOrIcon
                isLoading={loading}
                icon={<Assets.Spinner width={20} color="white" center={false} />}
                textColor={consent ? 'white' : 'black70'}
              >
                {t('common.confirm')}
              </ButtonTextOrIcon>
            </Button>
            {loadingSendFileReport === ThunkRequestStatus.Rejected && (
              <Feedback type="error" marginTop={60}>
                {t('errorMessages.generic.message')}
              </Feedback>
            )}
            {receiptResponse?.some(each => each.success === false) && (
              <Feedback type="error" marginTop={60} iconAbove>
                <FileReportTempTier1ErrorMessage />
                {devLogging &&
                  receiptResponse
                    .filter(each => each.success === false)
                    .map((each, i) => (
                      <Text key={i} marginTop={10} size="small">
                        S4I error message: {each.errorMessage}
                      </Text>
                    ))}
              </Feedback>
            )}
          </Container>
        </Panel.MainSection>

        <Panel.AsideSection>{renderAsideSection(faqs)}</Panel.AsideSection>
      </Panel.Body>
    </Panel>
  );
};

const renderAsideSection = (faqs: QuestionsAndAnswersType[]) => (
  <FAQ testId="test-storybook-faq" layout="side" questionAnswers={faqs} />
);
